import * as table from "./table-tools";
import { z } from "zod";
import { getIndexAbove } from "@tldraw/utils";
import type { BoardTable } from "./schema";
import type { TLShapeId, TLShape } from "@tldraw/tlschema";
import { checkedRows } from "./file-content";
import {
  tableColumnWidths,
  tableObjectHeight,
  replaceTableRows,
  insertTableRow,
  deleteTableRow,
  moveTableRow,
} from "./table-tools";
import type { OperationContext } from "./agent-operation-context";
const id = z.string().regex(/^shape:[\w-]{1,100}$/);
const rows = z
  .array(z.array(z.string().max(4000)).min(1).max(20))
  .min(1)
  .max(200);
const hex = z.string().regex(/^#[a-fA-F0-9]{6}$/);
const cellStyle = z
  .object({
    fontFamily: z.enum(["Arial", "Georgia", "monospace"]).optional(),
    fontSize: z.number().min(10).max(32).optional(),
    textColor: hex.optional(),
    fill: hex.optional(),
    border: hex.optional(),
    align: z.enum(["left", "center", "right"]).optional(),
    bold: z.boolean().optional(),
    italic: z.boolean().optional(),
  })
  .strict();
const cellRange = z
  .object({
    startRow: z.number().int().nonnegative(),
    startColumn: z.number().int().nonnegative(),
    endRow: z.number().int().nonnegative(),
    endColumn: z.number().int().nonnegative(),
  })
  .strict();
export const tableOperations = [
  z
    .object({
      type: z.literal("add_table"),
      parentId: z
        .string()
        .regex(/^(shape|page):[\w-]{1,100}$/)
        .optional(),
      id,
      sheetId: id.optional(),
      pageId: z
        .string()
        .regex(/^page:[\w-]{1,100}$/)
        .optional(),
      x: z.number().finite(),
      y: z.number().finite(),
      name: z.string().min(1).max(120),
      rows,
      headers: z.boolean().default(true),
      columnWidths: z.array(z.number().min(60).max(2000)).max(20).optional(),
      autoFit: z.boolean().default(true),
      layout: z.enum(["table", "grid"]).default("table"),
      w: z.number().positive().max(10000).optional(),
    })
    .strict(),
  z
    .object({
      type: z.literal("edit_table"),
      id,
      rows: rows.optional(),
      rowMap: z.array(z.number().int().min(0).nullable()).max(200).optional(),
      cells: z
        .array(
          z
            .object({
              row: z.number().int().min(0),
              column: z.number().int().min(0),
              text: z.string().max(4000),
            })
            .strict(),
        )
        .max(500)
        .optional(),
      rowOperations: z
        .array(
          z
            .object({
              action: z.enum(["insert", "delete", "move"]),
              index: z.number().int().min(0),
              to: z.number().int().min(0).optional(),
            })
            .strict(),
        )
        .max(50)
        .optional(),
      columnWidths: z.array(z.number().min(60).max(2000)).max(20).optional(),
      rowHeights: z.array(z.number().min(34).max(10000)).max(200).optional(),
      autoFit: z.boolean().optional(),
      headers: z.boolean().optional(),
      layout: z.enum(["table", "grid"]).optional(),
      globalStyle: cellStyle.optional(),
      range: cellRange.optional(),
      style: cellStyle.optional(),
      structure: z
        .object({
          action: z.enum([
            "insert_row",
            "delete_row",
            "move_row",
            "insert_column",
            "delete_column",
            "move_column",
            "sort",
          ]),
          index: z.number().int().nonnegative(),
          to: z.number().int().nonnegative().optional(),
          ascending: z.boolean().default(true),
        })
        .strict()
        .optional(),
      filterColumn: z.number().int().min(0).max(19).nullable().optional(),
      filterValue: z.string().max(120).optional(),
      groupColumn: z.number().int().min(0).max(19).nullable().optional(),
    })
    .strict(),
] as const;
const operation = z.discriminatedUnion("type", tableOperations);
export function applyTableOperation(input: unknown, c: OperationContext) {
  const parsed = operation.safeParse(input);
  if (!parsed.success) return false;
  const op = parsed.data;
  let shape: BoardTable;
  if (op.type === "add_table") {
    if (c.txn.get(op.id)) throw new Error("Shape ID already exists");
    const sheetId =
      op.sheetId ??
      (op.parentId?.startsWith("shape:") ? op.parentId : undefined);
    const pageId =
      op.pageId ?? (op.parentId?.startsWith("page:") ? op.parentId : undefined);
    if (Boolean(sheetId) === Boolean(pageId))
      throw new Error("Choose exactly one sheetId or pageId");
    const parent = sheetId ? c.editable(sheetId) : c.txn.get(pageId!);
    if (
      !parent ||
      (sheetId
        ? parent.typeName !== "shape" || parent.type !== "sheet"
        : parent.typeName !== "page")
    )
      throw new Error("Choose an existing sheet or page");
    if (parent.typeName !== "page" && parent.typeName !== "shape")
      throw new Error("Invalid parent");
    const siblings = [...c.txn.values()].filter(
      (r): r is TLShape => r.typeName === "shape" && r.parentId === parent.id,
    );
    shape = {
      id: op.id as TLShapeId,
      typeName: "shape",
      type: "table",
      parentId: parent.id,
      index: getIndexAbove(
        siblings
          .map((r) => r.index)
          .sort()
          .at(-1),
      ),
      x: op.x,
      y: op.y,
      rotation: 0,
      opacity: 1,
      isLocked: false,
      meta: {
        autoFitRows: op.autoFit,
        ...(op.w && !op.columnWidths
          ? {
              columnWidths: op.rows[0].map(() =>
                Math.max(60, op.w! / op.rows[0].length),
              ),
            }
          : {}),
      },
      props: {
        w: 240,
        h: 100,
        name: op.name,
        source: "",
        rows: checkedRows(op.rows),
        headers: op.headers,
        layout: op.layout,
      },
    };
  } else {
    const found = c.editable(op.id);
    if (found.type !== "table") throw new Error("Choose a table");
    shape = found;
  }
  let nextRows =
    op.type === "edit_table" && op.rows
      ? checkedRows(op.rows)
      : shape.props.rows;
  let meta =
    op.type === "edit_table" && op.rows
      ? replaceTableRows(shape.props.rows, nextRows, shape.meta, op.rowMap)
      : { ...shape.meta };
  if (op.type === "edit_table") {
    for (const edit of op.rowOperations ?? []) {
      if (
        edit.index >= nextRows.length ||
        (edit.action === "move" &&
          (edit.to === undefined || edit.to >= nextRows.length))
      )
        throw new Error("Table row index is out of range");
      const result =
        edit.action === "insert"
          ? insertTableRow(nextRows, meta, edit.index)
          : edit.action === "delete"
            ? deleteTableRow(nextRows, meta, edit.index)
            : moveTableRow(nextRows, meta, edit.index, edit.to!);
      nextRows = result.rows;
      meta = result.meta;
    }
    nextRows = nextRows.map((r) => [...r]);
    for (const cell of op.cells ?? []) {
      if (!nextRows[cell.row] || cell.column >= nextRows[cell.row].length)
        throw new Error("Table cell is out of range");
      nextRows[cell.row][cell.column] = cell.text;
    }
    if (op.rowHeights) meta.rowHeights = op.rowHeights;
  }
  const layout = op.layout ?? shape.props.layout;
  const headers =
    op.type === "edit_table" ? (op.headers ?? shape.props.headers) : op.headers;
  if (op.type === "edit_table") {
    if (op.structure) {
      const t = op.structure;
      const methods = {
        insert_row: table.insertTableRow,
        delete_row: table.deleteTableRow,
        move_row: table.moveTableRow,
        insert_column: table.insertTableColumn,
        delete_column: table.deleteTableColumn,
        move_column: table.moveTableColumn,
      };
      const result =
        t.action === "sort"
          ? table.sortTableModel(nextRows, meta, t.index, t.ascending, headers)
          : methods[t.action](nextRows, meta, t.index, t.to ?? t.index);
      nextRows = result.rows;
      meta = result.meta;
    }
    if (op.style) {
      if (!op.range)
        throw new Error("Cell formatting requires an explicit range.");
      meta = table.applyCellStyle(meta, nextRows, op.range, op.style);
    }
    if (
      op.filterColumn !== undefined ||
      op.filterValue !== undefined ||
      op.groupColumn !== undefined
    )
      meta.tableView = {
        ...table.savedTableView(meta, nextRows),
        ...(op.filterColumn !== undefined
          ? { filterColumn: op.filterColumn }
          : {}),
        ...(op.filterValue !== undefined
          ? { filterValue: op.filterValue }
          : {}),
        ...(op.groupColumn !== undefined
          ? { groupColumn: op.groupColumn }
          : {}),
      };
    if (op.globalStyle)
      for (const [key, value] of Object.entries(op.globalStyle))
        meta[key === "fill" ? "background" : key] = value;
  }
  if (op.columnWidths) meta.columnWidths = op.columnWidths;
  if (op.autoFit !== undefined) meta.autoFitRows = op.autoFit;
  const w = tableColumnWidths(meta, nextRows[0].length).reduce(
      (a, b) => a + b,
      0,
    ),
    h = tableObjectHeight(nextRows, meta, layout === "grid");
  table.validateTableMetadata(nextRows, meta);
  if (w > 10000 || h > 30000)
    throw new Error("Table is too large; widen columns or split the contents");
  c.set({
    ...shape,
    meta: meta as BoardTable["meta"],
    props: {
      ...shape.props,
      rows: checkedRows(nextRows),
      layout,
      headers,
      w: Math.max(240, w),
      h,
    },
  });
  return true;
}
